if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.onViewerSlide = (event) => {
          clearTimeout(this.slideTimer);
          this.slideTimer = setTimeout(() => {
            if (this.isConnected) this.onSlideChanged(event);
          }, 500);
        };
      }

      connectedCallback() {
        if (!this.elements.viewer) return;
        if (typeof ResizeObserver !== 'undefined') {
          this.galleryResizeObserver ||= new ResizeObserver(() => {
            const slider = this.elements.viewer.slider;
            if (!slider) return;
            const items = [...slider.children].filter(item => item.clientWidth > 0);
            const geometry = `${slider.clientWidth}:${items.length}:${items[1]?.offsetLeft || 0}:${items[0]?.clientWidth || 0}`;
            if (geometry === this.galleryWidth) return;
            this.galleryWidth = geometry;
            clearTimeout(this.slideTimer);
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => this.alignActiveMedia(), 0);
          });
          this.galleryResizeObserver.observe(this.elements.viewer);
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach(item => this.galleryResizeObserver.observe(item));
        }
        this.mobileLoopFrame = requestAnimationFrame(() => { if (this.isConnected) this.bindMobileLoop(); });
        this.elements.viewer.addEventListener('slideChanged', this.onViewerSlide);
        this.updateDesktopCounter();
        if (!this.elements.thumbnails) return;
        if (this.thumbnailEventsBound) return;
        this.thumbnailEventsBound = true;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if ((this.dataset.desktopLayout || '').includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.elements.viewer?.removeEventListener('slideChanged', this.onViewerSlide);
        clearTimeout(this.slideTimer);
        clearTimeout(this.scrollTimer);
        clearTimeout(this.resizeTimer);
        cancelAnimationFrame(this.mobileLoopFrame);
        this.mobileLoopCleanup?.();
        this.mobileLoopCleanup = null;
        this.galleryResizeObserver?.disconnect();
        this.galleryWidth = undefined;
        this.clearAnnouncement();
      }

      bindMobileLoop() {
        this.mobileLoopCleanup?.();
        const viewer = this.elements.viewer;
        if (viewer.dataset.galleryLoop !== 'true') return;
        let transition = null;
        let start = null;
        const sliderForGallery = () => viewer.querySelector('[id^="Slider-"]');
        const edges = () => {
          if (this.mql.matches && this.dataset.desktopLayout !== 'carousel') return null;
          const slider = sliderForGallery();
          if (!slider) return null;
          const items = [...slider.children].filter(item => item.hasAttribute('data-media-id') && item.clientWidth > 0);
          if (items.length < 2) return null;
          [items[0], items[items.length - 1]].forEach(item => item.querySelectorAll('img').forEach(image => { image.loading = 'eager'; }));
          return { slider, items, first: slider.scrollLeft <= items[0].offsetLeft + 3,
            last: slider.scrollLeft >= items[items.length - 1].offsetLeft - 3 };
        };
        const clear = () => {
          if (!transition) return;
          const previous = transition;
          transition = null;
          previous.animations?.forEach(animation => animation.cancel());
          previous.overlay.remove();
          viewer.style.position = previous.position;
        };
        const prepare = (edge, direction) => {
          if (transition) return null;
          const forward = direction > 0 && edge.last;
          const backward = direction < 0 && edge.first;
          if (!forward && !backward) return null;
          clearTimeout(this.slideTimer);
          const current = forward ? edge.items[edge.items.length - 1] : edge.items[0];
          const target = forward ? edge.items[0] : edge.items[edge.items.length - 1];
          const step = Math.abs(edge.items[1].offsetLeft - edge.items[0].offsetLeft);
          if (!step) return null;
          [current, target].forEach(item => { item.style.animation = 'none'; item.style.opacity = '1'; });
          const rect = edge.slider.getBoundingClientRect();
          const parentRect = viewer.getBoundingClientRect();
          const overlay = edge.slider.cloneNode(false);
          overlay.removeAttribute('id');
          overlay.removeAttribute('role');
          overlay.setAttribute('aria-hidden', 'true');
          overlay.inert = true;
          overlay.dataset.galleryTransition = '';
          overlay.style.cssText = `position:absolute;left:${rect.left - parentRect.left}px;top:${rect.top - parentRect.top}px;width:${rect.width}px;height:${rect.height}px;margin:0;padding:0;display:block;overflow:hidden;scroll-snap-type:none;pointer-events:none;z-index:4;list-style:none;`;
          const width = current.getBoundingClientRect().width;
          const copies = (forward ? [current, target] : [target, current]).map((item, index) => {
            const copy = item.cloneNode(true);
            [copy, ...copy.querySelectorAll('[id], [data-media-id]')].forEach(node => {
              node.removeAttribute('id');
              node.removeAttribute('data-media-id');
            });
            copy.querySelectorAll('script').forEach(script => script.remove());
            copy.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
            [copy, ...copy.querySelectorAll('.scroll-trigger')].forEach(node => node.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--fade-in', 'animate--slide-in'));
            copy.classList.add('is-active');
            copy.style.cssText = `position:absolute;left:${index * step}px;top:0;width:${width}px;min-width:${width}px;max-width:none;height:${rect.height}px;margin:0;display:block;opacity:1;animation:none;transition:none;scroll-snap-align:none;transform:translateX(${forward ? 0 : -step}px);`;
            overlay.append(copy);
            return copy;
          });
          const position = viewer.style.position;
          if (getComputedStyle(viewer).position === 'static') viewer.style.position = 'relative';
          viewer.append(overlay);
          transition = { overlay, copies, position, target, slider: edge.slider, step, direction, offset: forward ? 0 : -step, delta: 0 };
          return transition;
        };
        const move = dx => {
          if (!transition || transition.animations) return;
          transition.delta = transition.direction > 0 ? Math.max(-transition.step, Math.min(0, dx)) : Math.max(0, Math.min(transition.step, dx));
          transition.copies.forEach(copy => { copy.style.transform = `translateX(${transition.offset + transition.delta}px)`; });
        };
        const settle = commit => {
          const state = transition;
          if (!state || state.animations) return;
          const end = state.offset + (commit ? -state.direction * state.step : 0);
          const from = state.offset + state.delta;
          const finish = () => {
            if (transition !== state) return;
            if (commit && state.target.isConnected) {
              clearTimeout(this.slideTimer);
              this.setActiveMedia(state.target.dataset.mediaId, false, true);
              state.slider.scrollTo({ left: state.target.offsetLeft, behavior: 'instant' });
              viewer.update?.();
            }
            // Reveal the real slide only after the underlying scroll is painted.
            requestAnimationFrame(() => { if (transition === state) clear(); });
          };
          if (!state.copies[0].animate || matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
          const duration = Math.max(120, Math.round(280 * Math.abs(end - from) / state.step));
          state.animations = state.copies.map(copy => copy.animate(
            [{ transform: `translateX(${from}px)` }, { transform: `translateX(${end}px)` }],
            { duration, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' }
          ));
          Promise.all(state.animations.map(animation => animation.finished)).then(finish).catch(() => {});
        };
        const touchStart = event => {
          start = null;
          if (this.mql.matches || transition || event.touches.length !== 1 || (window.visualViewport?.scale || 1) > 1) return;
          const edge = edges();
          if (edge) start = { ...edge, x: event.touches[0].clientX, y: event.touches[0].clientY };
        };
        const touchMove = event => {
          if (!start || event.touches.length !== 1) { start = null; if (transition) settle(false); return; }
          const dx = event.touches[0].clientX - start.x;
          const dy = event.touches[0].clientY - start.y;
          if (!transition && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) { start = null; return; }
          if (!transition && Math.abs(dx) > Math.abs(dy) && ((start.last && dx < -8) || (start.first && dx > 8))) prepare(start, dx < 0 ? 1 : -1);
          if (transition) { event.preventDefault(); move(dx); }
        };
        const touchEnd = event => {
          if (!start || !event.changedTouches[0]) return;
          const dx = event.changedTouches[0].clientX - start.x;
          const dy = event.changedTouches[0].clientY - start.y;
          const commit = Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.2;
          if (!transition && commit) prepare(start, dx < 0 ? 1 : -1);
          if (transition) settle(commit && dx * transition.direction < 0);
          start = null;
        };
        const touchCancel = () => { start = null; if (transition) settle(false); };
        let lastWheel = 0;
        const wheel = event => {
          const now = performance.now();
          const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) >= 5;
          if (!horizontal) return;
          const newGesture = now - lastWheel > 220;
          lastWheel = now;
          if (transition) { event.preventDefault(); return; }
          if (!newGesture) return;
          const edge = edges();
          if (edge && prepare(edge, Math.sign(event.deltaX))) { event.preventDefault(); settle(true); }
        };
        const click = event => {
          const button = event.target.closest('button[name="previous"], button[name="next"]');
          if (!button || !viewer.contains(button)) return;
          if (transition) { event.preventDefault(); event.stopImmediatePropagation(); return; }
          const edge = edges();
          if (edge && prepare(edge, button.name === 'next' ? 1 : -1)) {
            event.preventDefault(); event.stopImmediatePropagation(); settle(true);
          }
        };
        const resize = () => { start = null; clear(); };
        edges(); // Warm both boundary images before the first wrap.
        this.addEventListener('touchstart', touchStart, { passive: true });
        this.addEventListener('touchmove', touchMove, { passive: false });
        this.addEventListener('touchend', touchEnd, { passive: true });
        this.addEventListener('touchcancel', touchCancel, { passive: true });
        this.addEventListener('wheel', wheel, { passive: false });
        this.addEventListener('click', click, true);
        window.addEventListener('resize', resize);
        this.mobileLoopCleanup = () => {
          clear();
          this.removeEventListener('touchstart', touchStart);
          this.removeEventListener('touchmove', touchMove);
          this.removeEventListener('touchend', touchEnd);
          this.removeEventListener('touchcancel', touchCancel);
          this.removeEventListener('wheel', wheel);
          this.removeEventListener('click', click, true);
          window.removeEventListener('resize', resize);
        };
      }

      alignActiveMedia() {
        if (!this.isConnected) return;
        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        const slider = this.elements.viewer.slider;
        if (!activeMedia || !slider || (this.mql.matches && !this.elements.thumbnails && this.dataset.desktopLayout !== 'carousel')) return;
        clearTimeout(this.slideTimer);
        slider.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
        this.elements.viewer.resetPages();
        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement?.isConnected) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === event.detail.currentElement);
        });
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateDesktopCounter();
      }

      updateDesktopCounter() {
        if (!this.mql.matches || (this.dataset.desktopLayout !== 'carousel' && !this.elements.viewer.classList.contains('gallery-viewer--desktop-overlay'))) return;
        const media = [...((this.elements.viewer.querySelector('[id^="Slider-Gallery-"]') || this.elements.viewer.querySelector('ul'))?.children || [])].filter(item => item.hasAttribute('data-media-id') && !item.hidden && !item.classList.contains('hidden'));
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        if (current) current.textContent = String(Math.max(1, media.findIndex(item => item.classList.contains('is-active')) + 1));
        if (total) total.textContent = String(media.length);
      }

      setActiveMedia(mediaId, prepend, preservePagePosition = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        const resolvedMediaId = activeMedia.dataset.mediaId;
        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${resolvedMediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstElementChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateDesktopCounter();
        this.preventStickyHeader();
        clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => {
          if (!this.isConnected || !activeMedia.isConnected) return;
          if (!this.mql.matches || this.elements.thumbnails || this.dataset.desktopLayout === 'carousel') {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (preservePagePosition || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${resolvedMediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        else this.clearAnnouncement();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        if (!thumbnail) return;
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      clearAnnouncement() {
        clearTimeout(this.announcementTimer);
        if (this.announcementImage && this.announcementLoad) {
          this.announcementImage.removeEventListener('load', this.announcementLoad);
        }
        this.announcementImage = null;
        this.announcementLoad = null;
        this.elements.liveRegion?.setAttribute('aria-hidden', true);
      }

      announceLiveRegion(activeItem, position) {
        this.clearAnnouncement();
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        const announce = () => {
          if (!this.isConnected || !activeItem.classList.contains('is-active')) return;
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          this.announcementTimer = setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        this.announcementImage = image;
        this.announcementLoad = announce;
        if (image.complete && image.naturalWidth > 0) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
